import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Search } from 'react-feather';
import { ConfigurableTable } from '../../../../../components/common/ConfigurableTable/ConfigurableTable';
import type { ColumnConfig } from '../../../../../hooks/useColumnConfig';
import {
  coachSlotCounts,
  doNotMatchIndex,
  effectiveSlots,
  isAvailabilityAdjusted,
  participantSlotCounts,
  searchParticipants,
  slotLabel,
  sourcePairKeys,
  unrecognizedDoNotMatch,
  unrecognizedDoNotMatchCount,
} from '../../model/derive';
import type { Participant } from '../../model/types';
import { useSetup } from '../../SetupContext';
import { AdjustAvailabilityModal } from './AdjustAvailabilityModal';
import { DnmCell } from './DnmCell';
import { SessionOnlyNote } from './SessionOnlyNote';
import classes from './DataStep.module.css';

const FIXED_COLUMNS: ColumnConfig[] = [
  { id: 'participant', label: 'Participant', visible: true, sortable: false },
  { id: 'availability', label: 'Availability', visible: true, sortable: false },
  { id: 'notes', label: 'Availability notes', visible: true, sortable: false },
  { id: 'dnm', label: 'Do not match', visible: true, sortable: false },
];

const DEFAULT_WIDTHS: Record<string, number> = {
  participant: 230,
  availability: 300,
  notes: 230,
  dnm: 300,
};
const ATTRIBUTE_WIDTH = 150;
const BAR_MAX_PX = 70;

/**
 * The chart sits above the table with one column per slot, lined up with the
 * availability dots in the table. It measures the first row's dots, so it
 * follows column resizing and horizontal scroll, and the pinned Participant
 * column's width becomes a gutter the bars slide under, like the dots do.
 * Positions go straight to CSS variables so scrolling doesn't re-render.
 */
function SlotChart({ table }: { table: HTMLDivElement | null }) {
  const { session, config } = useSetup();
  const bodyRef = useRef<HTMLDivElement>(null);
  const people = participantSlotCounts(
    session.participants,
    session.slots,
    config
  );
  const coaches = coachSlotCounts(session.coaches, session.slots, config);
  const max = Math.max(1, ...people);

  // No deps: rows, pages and column widths all move the dots, and each of
  // those re-renders this chart.
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body || !table) return;
    const align = () => {
      const dots = table.querySelector('[data-slot-dots]');
      // No rows (an empty search): keep the last position.
      if (!dots) return;
      const left = body.getBoundingClientRect().left;
      const pinned = table.querySelector('thead th')?.getBoundingClientRect();
      body.style.setProperty(
        '--slot-offset',
        `${dots.getBoundingClientRect().left - left}px`
      );
      body.style.setProperty(
        '--slot-gutter',
        `${Math.max(0, (pinned?.right ?? left) - left)}px`
      );
    };
    align();
    const observer = new ResizeObserver(align);
    observer.observe(table);
    observer.observe(body);
    // Scroll events don't bubble, so listen in the capture phase.
    table.addEventListener('scroll', align, true);
    return () => {
      observer.disconnect();
      table.removeEventListener('scroll', align, true);
    };
  });

  return (
    <Paper withBorder p="md" radius="md">
      <div ref={bodyRef} className={classes.chartBody}>
        <div className={classes.chartGutter}>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Availability by timeslot
          </Text>
          {session.coaches.length > 0 && (
            <Text size="xs" fw={600} c="dimmed">
              Coaches
            </Text>
          )}
        </div>
        <div className={classes.slotRow}>
          {session.slots.map((s, i) => (
            <div key={s} className={classes.slotCol}>
              <Text size="xs" fw={600}>
                {people[i]}
              </Text>
              <span
                className={classes.bar}
                style={{ height: Math.round((people[i] / max) * BAR_MAX_PX) }}
              />
              <Text size="xs" fw={600} mt={2} className={classes.slotName}>
                {slotLabel(s)}
              </Text>
            </div>
          ))}
        </div>
        {session.coaches.length > 0 && (
          <div className={`${classes.slotRow} ${classes.coachRow}`}>
            {session.slots.map((s, i) => (
              <Text
                key={s}
                className={classes.slotCol}
                fw={600}
                size="sm"
                c={coaches[i] === 0 ? 'dangerRed.6' : undefined}
              >
                {coaches[i]}
              </Text>
            ))}
          </div>
        )}
      </div>
    </Paper>
  );
}

interface ParticipantsTabProps {
  /** Show only people with unrecognized do-not-match emails (the Fix link turns this on). */
  onlyUnrecognized: boolean;
  onOnlyUnrecognizedChange: (value: boolean) => void;
}

export function ParticipantsTab({
  onlyUnrecognized,
  onOnlyUnrecognizedChange,
}: ParticipantsTabProps) {
  const { session, config, update, readOnly } = useSetup();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [adjusting, setAdjusting] = useState<Participant | null>(null);
  // State, not a ref: the table mounts after the chart, so the chart
  // re-renders once it can measure it.
  const [table, setTable] = useState<HTMLDivElement | null>(null);

  const columns = useMemo<ColumnConfig[]>(
    () => [
      ...FIXED_COLUMNS,
      ...session.columns.map(c => ({
        id: c.key,
        label: c.label,
        visible: true,
        sortable: false,
      })),
    ],
    [session.columns]
  );

  const dnmIndex = useMemo(
    () => doNotMatchIndex(session.participants, config),
    [session.participants, config]
  );
  const sourcePairs = useMemo(
    () => sourcePairKeys(session.participants),
    [session.participants]
  );
  const unrecognizedCount = unrecognizedDoNotMatchCount(
    session.participants,
    config
  );

  const filtered = searchParticipants(session.participants, query).filter(
    p => !onlyUnrecognized || unrecognizedDoNotMatch(p, config).length > 0
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pageCount);
  const rows = filtered.slice((current - 1) * pageSize, current * pageSize);
  const total = session.participants.length;

  const renderCell = (p: Participant, column: ColumnConfig) => {
    switch (column.id) {
      case 'participant':
        return (
          <Box miw={0}>
            <Text size="sm" fw={500}>
              {p.name}
            </Text>
            <Text size="xs" c="dimmed" className={classes.wrap}>
              {p.email}
            </Text>
          </Box>
        );
      case 'availability': {
        const slots = effectiveSlots(p, config);
        return (
          <Stack gap={8}>
            <Group gap={8} wrap="nowrap">
              <span
                className={classes.dots}
                data-slot-dots
                role="img"
                aria-label={`Available: ${
                  session.slots
                    .filter(s => slots.includes(s))
                    .map(slotLabel)
                    .join(', ') || 'no times'
                }`}
              >
                {session.slots.map(s => (
                  <span
                    key={s}
                    className={classes.dot}
                    data-on={slots.includes(s) || undefined}
                    title={slotLabel(s)}
                  />
                ))}
              </span>
              <Text size="sm" c="dimmed">
                {slots.length} of {session.slots.length}
              </Text>
            </Group>
            <Group gap={8}>
              {!readOnly && (
                <Button
                  size="compact-sm"
                  variant="default"
                  onClick={() => setAdjusting(p)}
                >
                  Adjust
                </Button>
              )}
              {isAvailabilityAdjusted(p, config) && (
                <Badge color="blue" variant="light">
                  Adjusted
                </Badge>
              )}
            </Group>
          </Stack>
        );
      }
      case 'notes':
        return p.availabilityNote ? (
          <Text size="sm">{p.availabilityNote}</Text>
        ) : (
          <Text size="sm" c="dimmed">
            No notes
          </Text>
        );
      case 'dnm':
        return (
          <DnmCell
            participant={p}
            partners={dnmIndex.get(p.id) ?? []}
            sourcePairs={sourcePairs}
          />
        );
      default: {
        const v = p.attributes[column.id];
        return (
          <Text size="sm">{Array.isArray(v) ? v.join(', ') : (v ?? '—')}</Text>
        );
      }
    }
  };

  return (
    <Stack gap="md">
      <div>
        <Title order={4}>Participants and timeslots</Title>
        <Text size="sm" c="dimmed" maw={820} mt={6}>
          Participant data comes from SGOS and is read-only, except availability
          and do-not-match. The first four columns are always here. The rest
          change by cohort. The chart shows how many people can make each
          timeslot. Do-not-match works both ways: adding someone to a person's
          list adds that person to theirs too.
        </Text>
      </div>
      <SessionOnlyNote>
        Availability and do-not-match changes are used for this matching session
        only. They aren't saved to SGOS, so update SGOS too if they should
        stick.
      </SessionOnlyNote>
      <SlotChart table={table} />
      <Group justify="space-between">
        <TextInput
          w={340}
          maw="100%"
          leftSection={<Search size={16} />}
          placeholder="Search participants by name or email"
          aria-label="Search participants"
          value={query}
          onChange={e => {
            setQuery(e.currentTarget.value);
            setPage(1);
          }}
        />
        <Group gap="lg">
          {(unrecognizedCount > 0 || onlyUnrecognized) && (
            <Switch
              size="sm"
              label={`Only people with unrecognized do-not-match emails (${unrecognizedCount})`}
              checked={onlyUnrecognized}
              onChange={e => {
                onOnlyUnrecognizedChange(e.currentTarget.checked);
                setPage(1);
              }}
            />
          )}
          <Text size="sm" c="dimmed">
            {filtered.length === total
              ? `${total} participants`
              : `${filtered.length} of ${total} participants`}
          </Text>
        </Group>
      </Group>
      <div ref={setTable}>
        <ConfigurableTable<Participant>
          data={rows}
          columns={columns}
          getRowKey={p => p.id}
          renderCell={renderCell}
          sortConfig={null}
          onSort={() => {}}
          page={current}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={size => {
            setPageSize(size);
            setPage(1);
          }}
          visibleColumnCount={columns.length}
          emptyMessage={
            onlyUnrecognized
              ? 'No one has an unrecognized do-not-match email.'
              : 'No participants match your search.'
          }
          stickyFirstColumn
          resizableColumns
          columnWidths={Object.fromEntries(
            columns.map(c => [
              c.id,
              widths[c.id] ?? DEFAULT_WIDTHS[c.id] ?? ATTRIBUTE_WIDTH,
            ])
          )}
          onColumnResize={(id, w) => setWidths(prev => ({ ...prev, [id]: w }))}
          onColumnResetWidth={id =>
            setWidths(prev => {
              const next = { ...prev };
              delete next[id];
              return next;
            })
          }
          getRowStyle={p =>
            p.availabilityNote && !isAvailabilityAdjusted(p, config)
              ? { background: 'var(--mantine-color-warningAmber-0)' }
              : undefined
          }
          entityLabel="participants"
        />
      </div>
      {adjusting && (
        <AdjustAvailabilityModal
          participant={adjusting}
          slots={session.slots}
          current={effectiveSlots(adjusting, config)}
          onClose={() => setAdjusting(null)}
          onSave={slots =>
            update({
              type: 'setParticipantSlots',
              id: adjusting.id,
              slots,
              sourceSlots: adjusting.sourceSlots,
            })
          }
        />
      )}
    </Stack>
  );
}
